/** @jsxImportSource @emotion/react */
import { StyledText } from "../../../assets/typography/typography";
import { css } from "@emotion/react";
import { ButtonAction, ButtonPrimary } from "../../../components/NewButtons";
import { FullScreenModal } from "../../../components/FullScreenModal";
import { SpinnerBlock } from "../../common/Spinner";
import { PlaceholderBox } from "../../../components/PlaceholderBox";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { colors } from "../../../components/colors";
import {
  AvailableMenu,
  CurrentMenus,
  Destination,
  PublishDestinations,
} from "../models/Menus";
import { useGetCountries } from "../../../service/api";
import { CollapseComponent } from "../../../components/CollapseComponent";
import { CollapseProps } from "antd";
import { Avatar } from "../../../components/Avatar";
import { SelectInput } from "../../../components/forms/inputs/SelectInput";
import { CircleContainer } from "../../../components/CircleContainer";
import { getCountryName, getCountryTextFlag } from "../menus-util";
import { Country } from "../../../model/Country";
import React from "react";
import { getHasChild } from "../menus-util";
import { useFormContext } from "react-hook-form";
import { useHandleFetchError } from "../../../error-handler";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  publishDestinations: PublishDestinations | undefined;
  isLoading: boolean;
  onSave: () => void;
};

const bottomWrapper = css`
  position: sticky;
  bottom: 0;
  display: flex;
  flex-direction: row;
  padding: 16px 24px;
  border-top: 1px solid ${colors.greyBorder};
  gap: 8px;
  align-items: center;
  height: fit-content;
  margin-top: auto;
  justify-content: space-between;
`;
const scrollInfoWrapper = css`
  display: flex;
  flex-direction: row;
  gap: 16px;
  align-items: center;
  width: max-content;
`;

const wrapperStyle = css`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 8px;
  overflow-y: scroll;
  padding: 24px 16px 16px 24px;
`;

const buttonWrapper = css`
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: end;
`;

const scrollLineBackgroundStyle = css`
  display: flex;
  min-height: 24px;
  min-width: 1px;
  height: 24px;
  width: 1px;
  background-color: ${colors.greyBorder};
`;

const scrollLineIndicatorStyle = css`
  display: flex;
  min-height: 50%;
  min-width: 1px;
  height: 50%;
  width: 1px;
  background-color: ${colors.textPrimary};
`;

const placeholderWrapperStyle = css`
  padding: 24px 24px 0px 24px;
`;

const collapseWrapperStyle = css`
  display: flex;
  flex-direction: row;
  width: 100%;
  align-items: center;
  gap: 16px;
`;

const infoWrapperStyle = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const selectInputWrapper = css`
  display: flex;
  flex-direction: row;
  width: 100%;
  max-width: 900px;
`;

const subInfoWrapperStyle = css`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const elementBaseStyle = css`
  display: flex;
  flex-direction: row;
  gap: 16px;
  align-items: center;
  justify-content: space-between;
`;

const salesChannelElementWrapperStyle = css`
  ${elementBaseStyle}
  margin-left: 64px;
  margin-right: 8px;
  padding-bottom: 16px;
  border-bottom: 1px solid ${colors.greyBorder};
  padding-top: 16px;
`;

const noSalesChannelsElementWrapperStyle = css`
  ${elementBaseStyle}
  padding: 8px;
`;

const avatarAndInfoWrapperStyle = css`
  display: flex;
  flex-direction: row;
  width: fit-content;
  min-width: max-content;
  align-items: center;
  gap: 16px;
`;

const getFile = (platform: string) => {
  switch (platform) {
    case "Foodora":
      return require("../../../assets/images/FoodoraLogo.png");
    case "JET":
      return require("../../../assets/images/JustEatLogo.png");
    case "Wolt":
      return require("../../../assets/images/WoltLogo.jpeg");
    case "HeapsGo":
      return require("../../../assets/images/HeapsGoLogo.png");
    default:
      return undefined;
  }
};

type ShopOrSalesChannelInformationProps = {
  destination: Destination;
  children?: React.ReactNode;
  countries: Country[] | undefined;
};

const ShopOrSalesChannelInformation = ({
  destination,
  children,
  countries,
}: ShopOrSalesChannelInformationProps) => {
  return (
    <>
      <Avatar imageUrl={destination.imageUrl} size="medium">
        {destination?.name?.charAt(0).toUpperCase()}
      </Avatar>
      <div css={infoWrapperStyle}>
        <div css={subInfoWrapperStyle}>
          <StyledText
            className="bodyTextSemibold mb-0"
            color={colors.textPrimary}
          >
            {destination.title ? destination.title : destination.name}
          </StyledText>
        </div>
        <div css={subInfoWrapperStyle}>
          {children}
          {children && (
            <CircleContainer
              height="4px"
              width="4px"
              backgroundColor={colors.greyBorder}
            />
          )}
          <StyledText
            className="bodySubtextRegular mb-0"
            color={colors.textPrimary}
          >
            {getCountryName(destination?.countryAlpha2Code, countries ?? [])}{" "}
            {getCountryTextFlag(destination?.countryAlpha2Code)}
          </StyledText>
        </div>
      </div>
    </>
  );
};

export const MutateMenusAndPushToSalesChannelsModal = ({
  isOpen,
  onClose,
  publishDestinations,
  isLoading,
  onSave,
}: Props) => {
  const {
    data: countries,
    isLoading: countriesAreLoading,
    error: getCountriesError,
  } = useGetCountries();
  const {
    formState: { dirtyFields },
  } = useFormContext();

  const isDirty = Object.keys(dirtyFields).length > 0;

  const getMenuOptions = (
    menus: AvailableMenu[],
    currentMenus: CurrentMenus[]
  ) => {
    const menuOptions: { label: string; value: string }[] = [
      {
        label: "No menu",
        value: "",
      },
    ];
    const availableMenus = menus.map((m) => {
      const currentMenu = currentMenus.find((cm) => cm.id === m.id);
      return {
        label: (
          <StyledText
            className={`${
              !!currentMenu ? "bodyTextMedium" : "bodyTextRegular"
            } mb-0`}
          >
            {!!currentMenu ? `${m.name} (Currently used)` : m.name}
          </StyledText>
        ),
        value: m.id,
      };
    });
    return [...menuOptions, ...availableMenus];
  };

  const items: CollapseProps["items"] = publishDestinations?.destinations
    .filter((destination) => {
      // If a destination does not have a child, we have to render non-collapsible row
      // Therefor we filter out destinations that does not have a child
      return getHasChild(destination, publishDestinations?.destinations);
    })
    .sort((a, b) => a.title?.localeCompare(b.title ?? "") ?? 0)
    .map((des, i) => ({
      key: des.id,
      label: (
        <div key={i} css={collapseWrapperStyle}>
          <ShopOrSalesChannelInformation
            destination={des}
            countries={countries}
          >
            <StyledText
              className="bodySubtextRegular mb-0"
              color={colors.textPrimary}
            >
              {publishDestinations?.destinations
                .filter(
                  (childDestination) => childDestination.parent === des.id
                )
                .reduce((acc, _) => acc + 1, 0)}{" "}
              sales channels
            </StyledText>
          </ShopOrSalesChannelInformation>
        </div>
      ),
      children: publishDestinations?.destinations
        // Get original index of destination in array to publish menu selection to form as I am reordering the array
        .map((childDestination, i) => ({
          fieldIndex: i,
          ...childDestination,
        }))
        // Get all destinations with parent id of destination
        .filter((childDestination) => childDestination.parent === des.id)
        .sort((a, b) => a.platform?.localeCompare(b.platform ?? "") ?? 0)
        // render children in collapse component
        .map((childDestination, index) => {
          const availableMenusRef = publishDestinations?.availableMenus.find(
            (a) => a.id === childDestination.availableMenusReference
          );
          return (
            <div key={index} css={salesChannelElementWrapperStyle}>
              <div css={avatarAndInfoWrapperStyle}>
                <Avatar
                  size="small"
                  rounded={false}
                  imageUrl={getFile(childDestination.platform)}
                >
                  {childDestination?.name?.charAt(0).toUpperCase()}
                </Avatar>
                <div css={infoWrapperStyle}>
                  <StyledText className="bodyTextSemibold mb-0">
                    {childDestination.platform
                      ? childDestination.platform
                      : childDestination.name}
                  </StyledText>
                  <StyledText
                    className="bodySubtextRegular mb-0"
                    color={colors.textSecondary}
                  >
                    {childDestination.platform
                      ? childDestination.name
                      : des.title}
                  </StyledText>
                </div>
              </div>
              <div css={selectInputWrapper}>
                <SelectInput
                  placeholder="Select new menu"
                  // For now only one menu per restaurant is allowed even though APIs support multi.
                  // So always replacing first item in array
                  // If relevant in future, utilize useFieldArray in this component instead of directly setting index 0.
                  name={`destinations.${
                    childDestination.fieldIndex
                  }.menus.${0}`}
                  options={getMenuOptions(
                    availableMenusRef?.menus || [],
                    childDestination.currentMenus
                  )}
                />
              </div>
            </div>
          );
        }),
    }));

  useHandleFetchError(getCountriesError);

  return (
    <FullScreenModal
      header="Push menus to sales channels for your restaurants"
      subtitle="You can push menus to sales channels for each restaurant below."
      isOpen={isOpen}
      onClose={onClose}
    >
      {isLoading || countriesAreLoading ? (
        <SpinnerBlock />
      ) : publishDestinations?.destinations?.length === 0 ? (
        <div css={placeholderWrapperStyle}>
          <PlaceholderBox
            header="No restaurants to publish menus to"
            subHeader="Create your first restaurant to sell your menus to customers."
            icon={<FontAwesomeIcon icon={solid("book-open")} />}
          />
        </div>
      ) : (
        <div css={wrapperStyle}>
          <CollapseComponent items={items} />
          {publishDestinations?.destinations
            // Get original index of destination in array to publish menu selection to form
            .map((destination, i) => ({
              fieldIndex: i,
              ...destination,
            }))
            // Find destinations that does not have a child and has no parent to display them as non-collapsible row
            .filter((destination) => {
              const hasChild = getHasChild(
                destination,
                publishDestinations?.destinations
              );
              return !hasChild && !destination.parent;
            })
            .sort((a, b) => a.title?.localeCompare(b.title ?? "") ?? 0)
            // Render non-collapsible row
            .map((destination, index) => {
              const availableMenusRef = publishDestinations?.availableMenus.find(
                (a) => a.id === destination.availableMenusReference
              );
              return (
                <div key={index} css={noSalesChannelsElementWrapperStyle}>
                  <div css={avatarAndInfoWrapperStyle}>
                    <ShopOrSalesChannelInformation
                      destination={destination}
                      countries={countries}
                    />
                  </div>
                  <div css={selectInputWrapper}>
                    <SelectInput
                      placeholder="Select new menu"
                      name={`destinations.${destination.fieldIndex}.menus.${0}`}
                      options={getMenuOptions(
                        availableMenusRef?.menus || [],
                        destination.currentMenus
                      )}
                    />
                  </div>
                </div>
              );
            })}
        </div>
      )}
      <div css={bottomWrapper}>
        <div css={scrollInfoWrapper}>
          <div css={scrollLineBackgroundStyle}>
            <div css={scrollLineIndicatorStyle} />
          </div>
          <StyledText
            color={colors.textSecondary}
            className="bodySubtextWide mb-0"
          >
            Scroll
          </StyledText>
        </div>
        <div css={buttonWrapper}>
          <ButtonPrimary onClick={() => onClose()} type="button">
            Cancel
          </ButtonPrimary>
          <ButtonAction type="button" onClick={onSave} disabled={!isDirty}>
            {isDirty ? "Push menus to sales channels" : "No changes made"}
          </ButtonAction>
        </div>
      </div>
    </FullScreenModal>
  );
};
